import { cva } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

const contentContainerRootVariant = cva(
  [
    "rounded-(--radius-sm)",
    "border border-outline-subtle",
    "bg-surface-container-lowest",
    "flex flex-col",
  ],
  {
    variants: {
      padding: {
        compact: "py-(--spacing-12) px-(--spacing-6)",
        standard: "p-(--spacing-12)",
      },
      elevated: {
        true: "shadow-elevation-1",
        false: null,
      },
    },
    defaultVariants: {
      padding: "standard",
      elevated: false,
    },
  },
);

const contentContainerBodyStyles = cva(
  [
    "w-full",
    "flex flex-col",
    "[overflow:clip]",
    "rounded-(--radius-sm)",
    "border-[0.5px] border-outline-subtle",
    "bg-surface-container-low",
    "text-text-default",
  ],
  {
    variants: {
      padding: {
        none: "p-0",
        compact: "p-(--spacing-8) gap-(--spacing-8)",
      },
    },
    defaultVariants: {
      padding: "compact",
    },
  },
);

const contentContainerTitleStyles = cva([
  "min-w-0 truncate",
  "font-(family-name:--font-family-title)",
  "text-(length:--font-size-title-md)",
  "font-medium",
  "leading-(--line-height-24)",
  "text-text-default",
]);

const contentContainerSummaryStyles = cva([
  "w-full min-w-0",
  "flex gap-(--spacing-24) items-start overflow-hidden",
]);

export interface ContentContainerRootProps
  extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  padding?: "compact" | "standard";
  elevated?: boolean;
}

/**
 * Shared visual shell for grouped content patterns.
 *
 * These internals provide the common root, header, title, summary, and body
 * slots. Consumers own their semantics and behavior, such as headings,
 * triggers, actions, disabled state, and focus treatment.
 *
 * @example
 * <ContentContainerRoot elevated>
 *   <ContentContainerHeader
 *     main={<ContentContainerTitle>Motor</ContentContainerTitle>}
 *     actions={<IconButton icon="ellipsis" aria-label="More actions" />}
 *     summary={<span>3 monitoring points</span>}
 *   />
 *   <ContentContainerBody>
 *     <AssetRow name="Drive end" />
 *   </ContentContainerBody>
 * </ContentContainerRoot>
 */
function ContentContainerRoot({
  children,
  className,
  padding,
  elevated,
  ...props
}: ContentContainerRootProps) {
  return (
    <div
      className={cn(
        contentContainerRootVariant({ padding, elevated }),
        className,
      )}
      {...props}
      data-slot="content-container-root"
    >
      {children}
    </div>
  );
}

export interface ContentContainerBodyProps
  extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  padding?: "none" | "compact";
}

function ContentContainerBody({
  children,
  className,
  padding,
  ...props
}: ContentContainerBodyProps) {
  return (
    <div
      className={cn(contentContainerBodyStyles({ padding }), className)}
      {...props}
      data-slot="content-container-body"
    >
      {children}
    </div>
  );
}

export interface ContentContainerFooterProps
  extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function ContentContainerFooter({
  children,
  className,
  ...props
}: ContentContainerFooterProps) {
  return (
    <div
      className={cn("w-full", className)}
      {...props}
      data-slot="content-container-footer"
    >
      {children}
    </div>
  );
}

export interface ContentContainerHeaderProps {
  /** Main header content. Consumers should pass the correct semantic element for their component. */
  main: ReactNode;
  start?: ReactNode;
  titleAccessory?: ReactNode;
  actions?: ReactNode;
  summary?: ReactNode;
}

function ContentContainerHeader({
  main,
  start,
  titleAccessory,
  actions,
  summary,
}: ContentContainerHeaderProps) {
  return (
    <div
      data-slot="content-container-header-wrapper"
      className="flex flex-col gap-(--spacing-8) w-full"
    >
      <div
        data-slot="content-container-header"
        className="flex items-center gap-(--spacing-8) w-full"
      >
        <div
          data-slot="content-container-header-left"
          className="flex flex-1 gap-(--spacing-8) items-center min-w-0"
        >
          {start && (
            <div
              data-slot="content-container-start-accessory"
              className="flex shrink-0 items-center"
            >
              {start}
            </div>
          )}
          <div
            data-slot="content-container-main"
            className="flex flex-1 min-w-0 items-center gap-(--spacing-8)"
          >
            {main}
            {titleAccessory && (
              <div
                data-slot="content-container-title-accessory"
                className="flex shrink-0 items-center"
              >
                {titleAccessory}
              </div>
            )}
          </div>
        </div>
        {actions && (
          <div
            data-slot="content-container-actions"
            className="flex shrink-0 gap-(--spacing-8) items-center justify-end"
          >
            {actions}
          </div>
        )}
      </div>
      {summary && (
        <div
          data-slot="content-container-summary"
          className={contentContainerSummaryStyles()}
        >
          {summary}
        </div>
      )}
    </div>
  );
}

export interface ContentContainerTitleProps
  extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
}

function ContentContainerTitle({
  children,
  className,
  ...props
}: ContentContainerTitleProps) {
  return (
    <span className={cn(contentContainerTitleStyles(), className)} {...props}>
      {children}
    </span>
  );
}

export {
  ContentContainerBody,
  ContentContainerFooter,
  ContentContainerHeader,
  ContentContainerRoot,
  ContentContainerTitle,
};
